import { Cart, ResponseError, nativeAppHeaders } from '@ynap/api-utils'
import { FETCH_CART_REQUEST, FETCH_CART_SUCCESS, FETCH_CART_ERROR } from '../constants/ActionTypes'
import { authActions } from '@ynap/auth-state'
import { getCommerceConfig, getClientRequestConfig } from '@ynap/config-state'
import { getWatsonConfig } from '@ynap/cart-state/src/selectors/getConfig'
import { getUberToken, getLocale } from '@ynap/cart-state'
import { raygun } from '@ynap/clientside-monitoring'
import wrapFetchWithJSON from './common/wrapFetchWithJSON'
const { createGuestIdentity } = authActions
import { push } from 'connected-react-router'
import { checkRectificationOnSuccess } from './cartRectification'
import { getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'
import { rectifyItemsByOrderMessage } from '../utils/rectifyItemsByOrderMessage'

// TODO: remove when WCS fixed there side so that applyCheckoutProfile
// gets applied internally if storeCredit is inconsistent after user
// changes country. See SPEOISNRS-3116
const storeCreditError = (cart = {}) => {
    const { usablePaymentInformation, paymentInstruction } = cart

    //only continue check on shipping-address step
    if (window.location.href.indexOf('/shipping-address') === -1) {
        return
    }

    const hasStoreCredit = usablePaymentInformation?.find((option) => {
        const isStoreCredit = option.paymentMethodName === 'STORE_CREDIT'
        const hasStoreCreditFunds = (option.xumet_availableAmount?.amount || 0) > 0
        return isStoreCredit && hasStoreCreditFunds
    })
    if (!hasStoreCredit) return false
    const usingStoreCredit = paymentInstruction?.find((option) => option.payMethodId === 'STORE_CREDIT')
    return hasStoreCredit && !!paymentInstruction && !usingStoreCredit
}

export default function fetchCart(options = {}) {
    const { recalculate = true, clearMessages = false, requiresEmailAddress = false } = options

    return (dispatch, getState, fetch) => {
        const state = getState()

        const fetchJSON = wrapFetchWithJSON(fetch, getClientRequestConfig(state).timeout)

        function getCart(count) {
            // We have to re-read the commerceConfig from the state since it could potentially have a guest uberToken
            // initialized if createGuestIdentity() was called
            const currentState = getState()
            const watsonConfig = getWatsonConfig(currentState)
            const commerceConfig = getCommerceConfig(currentState)
            const { langId, id } = getLocale(currentState)
            const additionalHeaders = {
                ...nativeAppHeaders(state.deviceType),
                ...getAdditionalCheckoutHeaders(state),
            }

            return fetchJSON(
                recalculate
                    ? Cart.orderCalculate({ ...commerceConfig, clearMessages, langId, additionalHeaders })
                    : Cart.getCart({ ...commerceConfig, langId, additionalHeaders })
            )
                .then((data) => {
                    const buyerId = data?.shoppingCart?.x_buyerId
                    if (buyerId) {
                        raygun.helpers.agent('setUser', { identifier: buyerId })
                    }
                    if (requiresEmailAddress && !data.shoppingCart.x_selfEmailAddress) {
                        return dispatch(push(`/${id}/checkout/login`))
                    } else {
                        // As per comment above, remove storeCredit integrity check
                        // when SPEOISNRS-3116 fixed in back end.
                        const scError = storeCreditError(recalculate ? data.shoppingCart : data)
                        if (scError) {
                            return fetchJSON(Cart.applyCheckoutProfile({ ...watsonConfig, langId, additionalHeaders })).then((data) => {
                                const cartSuccess = dispatch(fetchCartSuccess(data.shoppingCart))
                                checkRectificationOnSuccess(dispatch, data)
                                return cartSuccess
                            })
                        } else {
                            const rectifiedQtyItems = rectifyItemsByOrderMessage(data?.shoppingCart?.orderMessages)
                            const rectifiedQtyItemsList = rectifiedQtyItems?.map((item) => item.orderItemId)
                            const rectifiedShippingCart = rectifiedQtyItemsList?.length
                                ? {
                                      ...data.shoppingCart,
                                      rectifiedItemsList: rectifiedQtyItemsList,
                                      removedItems: data.shoppingCart.removedItems.filter((item) => rectifiedQtyItemsList.includes(Number(item.partNumber))),
                                  }
                                : data.shoppingCart
                            const cartSuccess = dispatch(fetchCartSuccess(recalculate ? rectifiedShippingCart : data))
                            checkRectificationOnSuccess(dispatch, data)
                            return cartSuccess
                        }
                    }
                })
                .catch((error) => handleError(error, count))
        }

        function handleError(error, count = 0) {
            if (error instanceof ResponseError && error.response.status === 401 && !count) {
                return dispatch(createGuestIdentity()).then(
                    () => getCart(count + 1),
                    (error) => handleError(error, count + 1)
                )
            }

            dispatch(fetchCartError(error))
            throw error
        }

        dispatch(fetchCartRequest())

        if (!getUberToken(state)) {
            return handleError(new ResponseError({ status: 401 }))
        }

        return getCart()
    }
}

function fetchCartRequest() {
    return {
        type: FETCH_CART_REQUEST,
    }
}

export function fetchCartSuccess(payload) {
    return {
        type: FETCH_CART_SUCCESS,
        payload,
    }
}

function fetchCartError(error) {
    return {
        type: FETCH_CART_ERROR,
        error,
    }
}
